import React, { useState, useEffect } from 'react';
import { useMemo } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import {
  CircularProgress,
  Alert,
  Autocomplete,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
  Tooltip,
  Box,
  Typography,
  Link,
  Popper,
} from '@mui/material';
import { ArrowLeft, Trash2, Save, Check, CornerUpLeft } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const calculateTotalPO = (products) => {
  return products.reduce(
    (total, product) => total + ((parseFloat(product.unitPrice) || 0) * product.quantityOrdered),
    0
  );
};

const POEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [poData, setPoData] = useState({
    poName: '',
    platform: '',
    products: [],
    masterPoId: null,
  });
  const [allProducts, setAllProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [warning, setWarning] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [casesData, setCasesData] = useState([]);
  const [caseQtyMap, setCaseQtyMap] = useState({});
  const [casesLoading, setCasesLoading] = useState(true);

  const location = useLocation();

  const productIdToShopifyMap = useMemo(() => {
    return allProducts.reduce((m, { id, productId }) => {
      m[id] = productId;
      return m;
    }, {});
  }, [allProducts]);

  const dbToShopifyMap = useMemo(() => {
    return allProducts.reduce((map, p) => {
      map[p.id.toString()] = p.productId.toString();
      return map;
    }, {});
  }, [allProducts]);

  useEffect(() => {
    console.log('🟣 caseQtyMap updated:', caseQtyMap);
  }, [caseQtyMap]);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [poRes, prodRes, casesRes] = await Promise.all([
          axios.get(`https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/pos/${id}`),
          axios.get(`https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/shopify-products`),
          axios.get(`https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/cases`),
        ]);

        console.group('🔍 FETCH DEBUG');
        console.log('📦 PRODUCTS', prodRes.data);
        console.log('📦 CASES', casesRes.data);
        console.groupEnd();

        setPoData(poRes.data);
        setAllProducts(prodRes.data);

        const map = casesRes.data.reduce((m, c) => {
          const sid = c.productId?.shopifyId;
          if (sid != null) m[sid.toString()] = c.quantity;
          return m;
        }, {});

        console.log('🔢 BUILT caseQtyMap', map);
        setCaseQtyMap(map);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
        setCasesLoading(false);
      }
    };
    fetchAll();
  }, [id]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (unsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [unsavedChanges]);

  const handleNavigation = (targetPath) => {
    if (unsavedChanges) {
      const userConfirmed = window.confirm("You have unsaved changes. Are you sure you want to leave this page?");
      if (!userConfirmed) return;
    }
    navigate(targetPath);
  };

  const goBack = () => {
    if (location.state && location.state.from) {
      navigate(location.state.from);
    } else if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/po-list');
    }
  };

  const handleSave = async () => {
    const t = toast.loading('Saving…');
    try {
      setSaving(true);
      setUnsavedChanges(false);
      await axios.put(`https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/pos/${id}`, poData);
      setTimeout(() => setSaving(false), 1000);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
      setShowSnackbar(true);
      toast.success('Purchase order saved', { id: t });
    } catch (error) {
      console.error('Error saving PO:', error);
      toast.error('Failed to save', { id: t });
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const t = toast.loading('Deleting…');
    try {
      await axios.delete(`https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/pos/${id}`);
      setShowDeleteModal(false);
      toast.success('Purchase order deleted', { id: t });
      navigate('/po-list');
    } catch (error) {
      console.error('Error deleting PO:', error);
      toast.error('Failed to delete', { id: t });
    }
  };

  const handleAddProduct = () => {
    if (!selectedProduct || quantity <= 0) {
      setWarning('Please select a product and enter a valid quantity.');
      return;
    }
    setWarning('');
    const productPrice = parseFloat(selectedProduct.cost) || 0;
    setPoData((prevData) => ({
      ...prevData,
      products: [
        ...prevData.products,
        {
          productId: selectedProduct.productId,
          productTitle: selectedProduct.title || 'No Title Available',
          quantityOrdered: parseInt(quantity, 10),
          unitPrice: productPrice,
        },
      ],
    }));
    setSelectedProduct(null);
    setQuantity('');
    setUnsavedChanges(true);
  };

  const handleRemoveProduct = (index) => {
    setPoData((prevData) => {
      const newProducts = [...prevData.products];
      newProducts.splice(index, 1);
      return { ...prevData, products: newProducts };
    });
    setUnsavedChanges(true);
  };

  const handleSnackbarClose = () => {
    setShowSnackbar(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} thickness={3} />
      </Box>
    );
  }

  if (!poData) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box className="fade-in">
      <Button
        onClick={goBack}
        startIcon={<ArrowLeft size={16} />}
        sx={{ mb: 2, color: 'text.secondary', fontWeight: 500 }}
      >
        Back
      </Button>

      <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', mb: 3 }}>
        Edit Purchase List
      </Typography>

      {poData.masterPoId && (
        <Box sx={{
          mb: 3, p: 2.5,
          border: '1px solid', borderColor: 'divider', borderRadius: 2,
        }}>
          <Typography variant="caption" sx={{
            fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
            fontSize: '0.7rem', color: 'text.secondary', display: 'block', mb: 0.5,
          }}>
            Purchase Order
          </Typography>
          <Link
            component={RouterLink}
            to={`/master-po/${poData.masterPoId._id}`}
            underline="hover"
            sx={{ fontSize: '1rem', fontWeight: 600, color: '#2383e2' }}
          >
            {poData.masterPoId.poName}
          </Link>
        </Box>
      )}

      <Box sx={{ mb: 3 }}>
        <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', display: 'block', mb: 0.5 }}>
          Purchase List Name
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={poData.poName}
          onChange={(e) => {
            setPoData({ ...poData, poName: e.target.value });
            setUnsavedChanges(true);
          }}
        />
      </Box>

      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Products</Typography>

      {warning && (
        <Box sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
          <Typography variant="body2" sx={{ color: '#b45309' }}>{warning}</Typography>
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: 2, mb: 3, alignItems: 'start' }}>
        <Autocomplete
          options={allProducts}
          getOptionLabel={(option) =>
            option.title ? option.title.replace(' - Default Title', '') : 'Unnamed Product'
          }
          value={selectedProduct}
          onChange={(event, newValue) => setSelectedProduct(newValue)}
          renderInput={(params) => (
            <TextField {...params} label="Search Product" fullWidth size="small" />
          )}
          PopperComponent={(props) => (
            <Popper {...props} placement="bottom-start" />
          )}
        />
        <TextField
          label="Quantity"
          type="number"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          fullWidth
          size="small"
        />
        <Button
          variant="contained"
          onClick={handleAddProduct}
          sx={{ bgcolor: '#2383e2', '&:hover': { bgcolor: '#1b6ec2' }, py: 1, px: 3 }}
        >
          Add
        </Button>
      </Box>

      {casesLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
            Loading case quantities…
          </Typography>
        </Box>
      )}

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mb: 3 }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '2fr 100px 100px 80px 50px 140px 80px',
          px: 2.5, py: 1.5,
          borderBottom: '1px solid', borderColor: 'divider',
          bgcolor: 'action.hover',
        }}>
          {['Product', 'Qty', 'Unit Cost', 'Total', '', 'Cases/Boxes', 'Case Qty'].map(h => (
            <Typography key={h} variant="caption" sx={{
              fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
              fontSize: '0.65rem', color: 'text.secondary',
            }}>{h}</Typography>
          ))}
        </Box>

        {poData.products.map((product, index) => {
          const rawPid = product.productId.toString();

          let shopifyKey;
          if (caseQtyMap.hasOwnProperty(rawPid)) {
            shopifyKey = rawPid;
          } else {
            shopifyKey = dbToShopifyMap[rawPid];
          }

          if (!shopifyKey) {
            console.warn(
              `⚠️ No shopifyKey for product.productId=${rawPid}`,
              { rawPid, dbToShopifyMap, caseQtyMapKeys: Object.keys(caseQtyMap) }
            );
          }

          const caseQty = casesLoading
            ? null
            : (caseQtyMap[shopifyKey] ?? 'N/A');

          const totalOrdered = product.quantityOrdered;
          const perCase = caseQty;
          const fullCases = perCase ? Math.floor(totalOrdered / perCase) : 0;
          const remainder = perCase ? totalOrdered % perCase : totalOrdered;

          return (
            <Box key={index} sx={{
              display: 'grid',
              gridTemplateColumns: '2fr 100px 100px 80px 50px 140px 80px',
              px: 2.5, py: 1.5,
              borderBottom: '1px solid', borderColor: 'divider',
              alignItems: 'center',
              '&:last-child': { borderBottom: 'none' },
              '&:hover': { bgcolor: 'action.hover' },
            }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {product.productTitle?.replace(' - Default Title', '') || 'Unnamed Product'}
              </Typography>

              <TextField
                type="number"
                value={product.quantityOrdered}
                onChange={e => {
                  const updated = [...poData.products];
                  updated[index].quantityOrdered = parseInt(e.target.value, 10);
                  setPoData({ ...poData, products: updated });
                  setUnsavedChanges(true);
                }}
                size="small"
                sx={{ '& .MuiInputBase-input': { textAlign: 'center' } }}
              />

              <TextField
                type="number"
                value={product.unitPrice}
                onChange={e => {
                  const updated = [...poData.products];
                  updated[index].unitPrice = parseFloat(e.target.value) || 0;
                  setPoData({ ...poData, products: updated });
                  setUnsavedChanges(true);
                }}
                size="small"
                sx={{ '& .MuiInputBase-input': { textAlign: 'center' } }}
              />

              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                ${(product.unitPrice * product.quantityOrdered).toFixed(2)}
              </Typography>

              <Tooltip title="Remove">
                <Button
                  size="small"
                  color="error"
                  onClick={() => handleRemoveProduct(index)}
                  sx={{ minWidth: 32, p: 0.5 }}
                >
                  <Trash2 size={15} />
                </Button>
              </Tooltip>

              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                {perCase
                  ? `${fullCases} case${fullCases !== 1 ? 's' : ''}` +
                    (remainder ? ` + ${remainder} box${remainder !== 1 ? 'es' : ''}` : '')
                  : '-'}
              </Typography>

              <Box sx={{ textAlign: 'center' }}>
                {casesLoading
                  ? <CircularProgress size={14} />
                  : <Typography variant="body2" sx={{ fontWeight: 600 }}>{caseQty}</Typography>
                }
              </Box>
            </Box>
          );
        })}

        {poData.products.length > 0 && (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '2fr 100px 100px 80px 50px 140px 80px',
            px: 2.5, py: 1.5,
            bgcolor: 'action.hover',
          }}>
            <Typography variant="body2" sx={{ fontWeight: 700, gridColumn: 'span 3', textAlign: 'right', pr: 2 }}>
              Purchase List Total
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              ${calculateTotalPO(poData.products).toFixed(2)}
            </Typography>
            <Box /><Box /><Box />
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || success}
          startIcon={saving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : success ? <Check size={16} /> : <Save size={16} />}
          sx={{
            bgcolor: success ? '#10b981' : '#2383e2',
            '&:hover': { bgcolor: success ? '#059669' : '#1b6ec2' },
          }}
        >
          {saving ? 'Saving...' : success ? 'Saved' : 'Save Changes'}
        </Button>
        {poData.masterPoId && (
          <Button
            variant="outlined"
            startIcon={<CornerUpLeft size={16} />}
            onClick={() => navigate(`/master-po/${poData.masterPoId._id}`)}
          >
            Return to PO
          </Button>
        )}
      </Box>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Changes saved successfully!
        </Alert>
      </Snackbar>

      <Dialog
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this purchase list? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default POEdit;